import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Target, Eye, Heart, Lightbulb, Users, Sparkles, Rocket, Store } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import GlitterIcon from "../components/GlitterIcon";

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );
    const timer = setTimeout(() => els.forEach((el) => observer.observe(el)), 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

const valueIcons = [Heart, CheckCircle, Users, Lightbulb];
const valueColors = [
  { icon: "text-rose-500", bg: "bg-rose-50" },
  { icon: "text-primary-600", bg: "bg-primary-50" },
  { icon: "text-amber-500", bg: "bg-amber-50" },
  { icon: "text-secondary-600", bg: "bg-secondary-50" },
];

// Journey timeline — one icon per milestone, matching the milestones array order
const milestoneIcons = [Lightbulb, Rocket, Store];

const About = () => {
  const { t } = useLanguage();
  const a = t.about;
  useScrollReveal();

  return (
    <div className="">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-white via-slate-50 to-gray-100">
        {/* Silver shimmer blobs — matches Home hero */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-primary-100/60 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-slate-200/80 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-64 h-64 bg-secondary-100/50 rounded-full blur-3xl"></div>
        </div>
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-full px-4 py-2 mb-6 shadow-sm">
            <GlitterIcon color="red" />
            <span className="text-sm font-medium text-primary-700">{a.hero.badge}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-5 text-gray-900">
            {a.hero.title} <br />
            <span className="bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent">{a.hero.titleHighlight}</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
            <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
            <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
          </div>
          <p className="text-gray-600 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed">{a.hero.subtitle}</p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="min-h-screen flex items-center py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Our Foundation
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              <span className="block mb-2">Mission &amp; Vision</span>
            </h2>
            <div className="flex items-center justify-center gap-3">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="reveal stagger-1 bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-3xl p-6 lg:p-8 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center mb-4">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">{a.mission.title}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{a.mission.body}</p>
              <ul className="space-y-2">
                {a.mission.points.map((pt: string, i: number) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal stagger-2 bg-gradient-to-br from-secondary-50 to-secondary-100 border border-secondary-200 rounded-3xl p-6 lg:p-8 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 bg-secondary-600 rounded-2xl flex items-center justify-center mb-4">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-3">{a.vision.title}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{a.vision.body}</p>
              <ul className="space-y-2">
                {a.vision.points.map((pt: string, i: number) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="min-h-screen flex items-center py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              {a.story.badge}
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">{a.story.title}</span>
              <span className="gradient-text">{a.story.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="reveal stagger-1">
              <p className="text-gray-700 leading-relaxed mb-3">{a.story.p1}</p>
              <p className="text-gray-700 leading-relaxed mb-3">{a.story.p2}</p>
              <p className="text-gray-700 leading-relaxed mb-4">{a.story.p3}</p>
              <blockquote className="border-l-4 border-primary-500 pl-5 py-2 bg-primary-50 rounded-r-xl">
                <p className="text-primary-800 italic font-medium mb-1.5">"{a.story.quote}"</p>
                <cite className="text-primary-600 text-sm font-semibold">— {a.story.quoteAuthor}</cite>
              </blockquote>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {a.story.stats.map((s: any, i: number) => (
                  <div key={i} className="text-center bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="font-heading text-3xl font-bold text-primary-600 mb-1">{s.value}</div>
                    <div className="text-gray-600 text-sm font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <h4 className="font-heading font-bold text-gray-900 mb-3">{a.story.differentiators.title}</h4>
                <ul className="space-y-2">
                  {a.story.differentiators.points.map((pt: string, i: number) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="min-h-screen flex items-center py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
              Our Principles
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">{a.values.title}</span>
              <span className="gradient-text">{a.values.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-secondary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">{a.values.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.values.items.map((val: any, i: number) => {
              const Icon = valueIcons[i];
              const colors = valueColors[i];
              return (
                <div key={i} className={`reveal card text-center group stagger-${i+1}`}>
                  <div className={"w-14 h-14 " + colors.bg + " rounded-2xl flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition-transform duration-300"}>
                    <Icon className={"w-7 h-7 " + colors.icon} />
                  </div>
                  <h3 className="font-heading font-bold text-gray-900 text-lg mb-3">{val.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{val.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Our Journey
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">{a.milestones.title}</span>
              <span className="gradient-text">{a.milestones.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">{a.milestones.subtitle}</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-0.5 bg-primary-200 hidden lg:block"></div>
            <div className="space-y-10">
              {a.milestones.items.map((m: any, i: number) => {
                const MIcon = milestoneIcons[i] || Sparkles;
                return (
                  <div key={i} className={"reveal flex flex-col lg:flex-row items-center gap-8 " + (i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse")}>
                    <div className="lg:w-1/2 lg:text-right">
                      {i % 2 === 0 && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 lg:mr-10">
                          <div className="inline-block bg-primary-100 text-primary-700 rounded-full px-3 py-1 text-sm font-semibold mb-3">{m.year}</div>
                          <h3 className="font-heading font-bold text-gray-900 text-lg mb-2">{m.title}</h3>
                          <p className="text-gray-600 text-sm">{m.desc}</p>
                        </div>
                      )}
                    </div>
                    <div className="hidden lg:flex w-12 h-12 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full items-center justify-center flex-shrink-0 z-10 shadow-lg">
                      <MIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="lg:w-1/2">
                      {i % 2 !== 0 && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 lg:ml-10">
                          <div className="inline-block bg-primary-100 text-primary-700 rounded-full px-3 py-1 text-sm font-semibold mb-3">{m.year}</div>
                          <h3 className="font-heading font-bold text-gray-900 text-lg mb-2">{m.title}</h3>
                          <p className="text-gray-600 text-sm">{m.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Leadership
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">{a.team.title}</span>
              <span className="gradient-text">{a.team.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">{a.team.subtitle}</p>
          </div>

          {/* Board of Directors */}
          <div className="mb-14">
            <h3 className="font-heading text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-primary-500 inline-block"></span>
              {a.team.directors.sectionTitle}
              <span className="w-8 h-0.5 bg-primary-500 inline-block"></span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {a.team.directors.placeholder.map((member: any, i: number) => (
                <div key={i} className={`reveal group bg-white border border-gray-100 rounded-2xl p-6 flex gap-5 items-start hover:shadow-xl hover:-translate-y-1 transition-all duration-300 stagger-${i + 1}`}>
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-heading font-bold text-xl">{member.initial}</span>
                  </div>
                  <div>
                    <div className="font-heading font-bold text-gray-900 text-base mb-0.5">{member.name}</div>
                    <div className="text-primary-600 text-sm font-semibold mb-2">{member.designation}</div>
                    <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Operations Team */}
          <div>
            <h3 className="font-heading text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-secondary-500 inline-block"></span>
              {a.team.operations.sectionTitle}
              <span className="w-8 h-0.5 bg-secondary-500 inline-block"></span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {a.team.operations.placeholder.map((member: any, i: number) => (
                <div key={i} className={`reveal group bg-white border border-gray-100 rounded-2xl p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 stagger-${i + 1}`}>
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary-500 to-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-heading font-bold text-xl">{member.initial}</span>
                  </div>
                  <div className="font-heading font-bold text-gray-900 text-sm mb-0.5">{member.name}</div>
                  <div className="text-secondary-600 text-xs font-semibold mb-3">{member.designation}</div>
                  <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-gray-400 italic">* Team profiles are being updated. Our leadership team will be featured here soon.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-6">{a.cta.title}</h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10">{a.cta.subtitle}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/investors" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              {a.cta.btn1}
            </Link>
            <Link to="/franchise" className="btn-outline">
              {a.cta.btn2} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="border-2 border-white/50 text-white hover:border-white font-semibold py-3 px-8 rounded-lg transition-all">
              {a.cta.btn3}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
